if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      cachedProductInfo = new Map();
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initDescriptionModal();
        this.initMetafieldAccordion();
        initLowyaStyleReviews();
        
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;

        // Combined listings need a product swap instead of a section-only refresh.
        if (shouldSwapProduct) {
          const shouldFetchFullPage = this.dataset.updateUrl === 'true';
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
            targetId: target.id,
            callback: this.handleSwapProduct(productUrl, shouldFetchFullPage),
          });
          return;
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
          targetId: target.id,
          callback: this.handleUpdateProductInfo(productUrl, selectedOptionValues),
        });
      }

      updatePrice(price, compareAtPrice) {
        const priceWrapper = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceWrapper) return;

        const priceContainer = priceWrapper.querySelector('.price');
        const priceElement = priceWrapper.querySelector('.price__current');
        const compareElement = priceWrapper.querySelector('.price__compare');
        const saleLabelElement = priceWrapper.querySelector('.sale-label');

        if (priceElement) {
          priceElement.innerHTML = this.formatMoney(price);
        }

        // セール判定
        const isOnSale = compareAtPrice && compareAtPrice > price;

        // SALEラベルの表示/非表示
        if (saleLabelElement) {
          saleLabelElement.style.display = isOnSale ? '' : 'none';
        }

        if (compareElement) {
          if (isOnSale) {
            compareElement.innerHTML = this.formatMoney(compareAtPrice);
            compareElement.style.display = '';
            priceElement?.classList.add('price__current--on-sale');
          } else {
            compareElement.style.display = 'none';
            priceElement?.classList.remove('price__current--on-sale');
          }
        }

        // priceラッパーのクラス更新
        if (priceContainer) {
          if (isOnSale) {
            priceContainer.classList.add('price--on-sale');
          } else {
            priceContainer.classList.remove('price--on-sale');
          }
        }
      }

      updateAvailability(variant) {
        if (this.productForm && typeof this.productForm.updateSubmitButtonForVariant === 'function') {
          this.productForm.updateSubmitButtonForVariant(variant);
        }
      }

      formatMoney(cents) {
        if (typeof cents !== 'number') return '';
        return Shopify.formatMoney(cents, Shopify.money_format);
      }

      resetProductFormState() {
        const productForm = this.productForm;
        if (productForm && typeof productForm.toggleSubmitButton === 'function') {
          productForm.toggleSubmitButton(true);
        }
        if (productForm && typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage();
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();

        const cachedResponse = this.cachedProductInfo.get(requestUrl);
        if (cachedResponse) {
          const html = new DOMParser().parseFromString(cachedResponse, 'text/html');
          callback(html);
          this.focusSelectedOption(targetId);
          return;
        }

        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            this.cachedProductInfo.set(requestUrl, responseText);
            if (this.cachedProductInfo.size > 30) {
              this.cachedProductInfo.delete(this.cachedProductInfo.keys().next().value);
            }
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            this.focusSelectedOption(targetId);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      focusSelectedOption(targetId) {
        const target = document.querySelector(`#${targetId}`);
        if (!target) return;

        try {
          target.focus({ preventScroll: true });
        } catch (error) {
          target.focus();
        }
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        if (!params.length) return url;

        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl, optionValueIds = []) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id, optionValueIds);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.updateAvailability(variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId, optionValueIds = []) {
        const params = variantId
          ? `variant=${variantId}`
          : optionValueIds.length
            ? `option_values=${optionValueIds.join(',')}`
            : '';

        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${params ? `?${params}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${params ? `?${params}` : ''}`);
      }

      setUnavailable() {
        if (this.productForm && typeof this.productForm.toggleSubmitButton === 'function') {
          this.productForm.toggleSubmitButton(true, window.variantStrings.unavailable);
        }

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      initDescriptionModal() {
        const toggleButtons = this.querySelectorAll('[data-description-toggle]');
        const descriptionWrappers = this.querySelectorAll('.product__description-wrapper');
        const descriptionContents = this.querySelectorAll('[data-description-content]');

        if (toggleButtons.length === 0 || descriptionWrappers.length === 0) return;

        // 通常表示エリアの画像すべてにloading="lazy"を追加
        descriptionContents.forEach(content => {
          const images = content.querySelectorAll('img');
          images.forEach(img => {
            img.setAttribute('loading', 'lazy');
          });
        });

        // 高さ判定: 600pxを超える場合のみボタンを表示
        const checkHeight = () => {
          descriptionContents.forEach((content, index) => {
            const height = content.scrollHeight;
            const button = toggleButtons[index];
            
            if (button) {
              if (height > 600) {
                button.style.display = 'block';
              } else {
                button.style.display = 'none';
              }
            }
          });
        };

        // 初期チェック
        checkHeight();

        // 画像読み込み後に再チェック
        const images = Array.from(descriptionContents).flatMap(content => 
          Array.from(content.querySelectorAll('img'))
        );
        
        if (images.length > 0) {
          Promise.all(
            images.map(img => {
              if (img.complete) return Promise.resolve();
              return new Promise(resolve => {
                img.addEventListener('load', resolve);
                img.addEventListener('error', resolve);
              });
            })
          ).then(() => {
            checkHeight();
          });
        }

        // アコーディオン処理
        toggleButtons.forEach((button, index) => {
          const wrapper = descriptionWrappers[index];
          const toggleText = button.querySelector('[data-toggle-text]');
          
          if (!wrapper || !toggleText) return;

          button.addEventListener('click', () => {
            const isExpanded = wrapper.classList.contains('is-expanded');
            const collapsedText = button.dataset.collapsedText || 'もっと見る';
            const expandedText = button.dataset.expandedText || '閉じる';

            if (isExpanded) {
              // 閉じる
              wrapper.classList.remove('is-expanded');
              toggleText.textContent = collapsedText;
            } else {
              // 開く
              wrapper.classList.add('is-expanded');
              toggleText.textContent = expandedText;
            }
          });
        });
      }

      initMetafieldAccordion() {
        const accordions = this.querySelectorAll('[data-accordion-trigger]');
        
        accordions.forEach((trigger) => {
          const accordion = trigger.closest('.product__metafield-accordion');
          
          trigger.addEventListener('click', () => {
            const isOpen = accordion.classList.contains('is-open');
            
            if (isOpen) {
              accordion.classList.remove('is-open');
            } else {
              accordion.classList.add('is-open');
            }
          });
        });
      }
    }
  );
}

function initLowyaStyleReviews() {
  if (window.utopieReviewWidgetObserverStarted) return;
  window.utopieReviewWidgetObserverStarted = true;

  const enhanceReviewWidget = () => {
    document.querySelectorAll('.jdgm-rev-widg:not(.utopie-review-widget)').forEach((widget) => {
      const header = widget.querySelector('.jdgm-rev-widg__header');
      const title = widget.querySelector('.jdgm-rev-widg__title');
      const summary = widget.querySelector('.jdgm-rev-widg__summary');
      const summaryText = widget.querySelector('.jdgm-rev-widg__summary-text');
      const averageRating = parseFloat(widget.dataset.averageRating || '0');
      const reviewCount = parseInt(widget.dataset.numberOfReviews || '0', 10);

      if (!header || !title || !summary || !averageRating || !reviewCount) return;

      widget.classList.add('utopie-review-widget');
      title.textContent = 'みんなのレビュー';

      if (!header.querySelector('.utopie-review-count')) {
        const count = document.createElement('span');
        count.className = 'utopie-review-count';
        count.textContent = `(${reviewCount}件)`;
        title.insertAdjacentElement('afterend', count);
      }

      if (!summary.querySelector('.utopie-review-average')) {
        const average = document.createElement('span');
        average.className = 'utopie-review-average';
        average.textContent = averageRating.toFixed(1);
        summary.prepend(average);
      }

      if (summaryText) {
        summaryText.innerHTML = `${reviewCount}件のレビューに<br class="utopie-review-summary-break">基づいています`;
      }

      widget.querySelectorAll('.jdgm-histogram__row[data-rating]').forEach((row) => {
        const starLabel = row.querySelector('.jdgm-histogram__star');
        const frequency = row.querySelector('.jdgm-histogram__frequency');

        if (starLabel) {
          starLabel.dataset.rating = row.dataset.rating;
        }

        if (frequency) {
          const frequencyValue = row.dataset.frequency || frequency.textContent.trim();
          const percentageValue = row.dataset.percentage;
          frequency.textContent = percentageValue ? `${frequencyValue}件（${percentageValue}%）` : `${frequencyValue}件`;
        }
      });
    });
  };

  enhanceReviewWidget();

  const observer = new MutationObserver(enhanceReviewWidget);
  observer.observe(document.body, { childList: true, subtree: true });
}
